if (!customElements.get('product-form-component')) {
  let cartNavigationPending = false;
  customElements.define(
    'product-form-component',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.init();
      }

      init() {
        this.form = this.querySelector('form');
        if (!this.form) return;
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-drawer');
        this.submitButton = this.form?.querySelector('.add-to-cart-button');
        this.submitButtonText = this.submitButton ? this.submitButton.querySelector('span') : null;
        this.checkbox = this.form?.querySelector('[id^="agree_condition-"]');
        this.buyItNowButton = this.form?.querySelector('.shopify-payment-button');

        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.initAgreeCondition();

        // Sync error messages to sticky add-to-cart error area (same section)
        this.setupErrorSync();
      }

      setupErrorSync() {
        try {
          const productInfo = this.closest('product-info');
          const sectionId = productInfo?.dataset.section || productInfo?.dataset.originalSection;
          if (!sectionId) return;

          const stickyATC = document.querySelector(`sticky-atc[data-sticky-section-id="${sectionId}"]`);
          if (!stickyATC) return;

          const mainErrorWrapper = this.querySelector('.product-form__error-message-wrapper');
          const stickyErrorWrapper = stickyATC.querySelector('.product-form__error-message-wrapper');
          if (!mainErrorWrapper || !stickyErrorWrapper) return;

          const syncFromMain = () => {
            const isVisible = !mainErrorWrapper.hasAttribute('hidden');
            const errorMessage = mainErrorWrapper.querySelector('.product-form__error-message')?.textContent;
            stickyErrorWrapper.toggleAttribute('hidden', !isVisible || !errorMessage);
            if (isVisible && errorMessage) {
              const stickyErrorMessage = stickyErrorWrapper.querySelector('.product-form__error-message');
              if (stickyErrorMessage) stickyErrorMessage.textContent = errorMessage;
            }
          };

          // Initial sync
          syncFromMain();

          // Observe changes on main error wrapper
          this._errorObserver = new MutationObserver(syncFromMain);
          this._errorObserver.observe(mainErrorWrapper, {
            attributes: true,
            attributeFilter: ['hidden'],
            childList: true,
            subtree: true,
          });

          // Also listen to cartError pub/sub to copy message
          this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, () => {
            syncFromMain();
          });
        } catch (e) {
          // silent
        }
      }

      initAgreeCondition() {
        if (!this.checkbox || !this.buyItNowButton) return;

        this.buyItNowButton.classList.add('disabled');

        this.checkbox.addEventListener('change', () => {
          this.buyItNowButton.classList.toggle('disabled', !this.checkbox.checked);
        });

        this.form.addEventListener('submit', (e) => {
          if (!this.checkbox.checked) {
            e.preventDefault();
          }
        });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (cartNavigationPending) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        if (this.checkbox && !this.checkbox.checked) return;

        this.handleErrorMessage();
        this.error = false;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.submitButton.querySelector('.loading__spinner').classList.remove('hidden');

        // Also reflect loading state on the sticky add-to-cart button (if present)
        const productInfo = this.closest('product-info');
        const sectionId = productInfo?.dataset.section || productInfo?.dataset.originalSection;
        const stickyAddButton = sectionId
          ? document.getElementById(`StickyCart-ProductSubmitButton-${sectionId}`)
          : undefined;
        const stickyLoadingSpinner = stickyAddButton?.querySelector('.loading__spinner');
        if (stickyAddButton && stickyLoadingSpinner) {
          stickyAddButton.classList.add('loading');
          stickyLoadingSpinner.classList.remove('hidden');
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        const properties = (productInfo || this).querySelectorAll('.product-form__input [name^="properties"]');

        properties.forEach(property => {
          if (property.disabled || property.matches(':disabled') || property.form) return;
          if ((property.type === 'checkbox' || property.type === 'radio') && !property.checked) return;
          if (property.type === 'file') {
            if (!property.files || property.files.length === 0 || !property.files[0]) return;
            formData.append(property.name, property.files[0]);
            return;
          }

          if (property.value == null || property.value === '') return;
          formData.append(property.name, property.value);
        });

        config.body = formData;

        this.requestCartJSON(config, formData.get('id'))
          .then(async (response) => {
            if (response.status || response.errors) {
              const errorMessage = response.description || response.errors || response.message || window.cartStrings.error;
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form-component',
                productVariantId: formData.get('id'),
                errors: errorMessage,
                message: response.message,
              });
              this.handleErrorMessage(errorMessage);
              if (!(await this.reconcileRejectedCart())) {
                this.recoverUncertainCart();
                return;
              }

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = `${window.routes?.cart_url}`;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form-component',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              let cartRendered = false;
              let fallbackTimer;
              const renderCart = () => {
                if (cartRendered) return;
                cartRendered = true;
                if (fallbackTimer) clearTimeout(fallbackTimer);
                requestAnimationFrame(() => {
                  if (this.cart && typeof this.cart.renderContents === 'function') {
                    this.cart.renderContents(response);
                  }
                });
              };

              document.body.addEventListener('modalClosed', renderCart, { once: true });
              fallbackTimer = setTimeout(renderCart, 250);
              quickAddModal.hide(true);
            } else {
              if (this.cart && typeof this.cart.renderContents === 'function') {
                this.cart.renderContents(response);
              }
            }
          })
          .catch((e) => {
            this.recoverUncertainCart();
            console.error(e);
          })
          .finally(() => {
            if (this.cartNavigationPending) return;
            this.submitButton.classList.remove('loading');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.submitButton.querySelector('.loading__spinner').classList.add('hidden');

            // Clear loading state on the sticky add-to-cart button (if present)
            const productInfo = this.closest('product-info');
            const sectionId = productInfo?.dataset.section || productInfo?.dataset.originalSection;
            const stickyAddButton = sectionId
              ? document.getElementById(`StickyCart-ProductSubmitButton-${sectionId}`)
              : undefined;
            const stickyLoadingSpinner = stickyAddButton?.querySelector('.loading__spinner');
            if (stickyAddButton && stickyLoadingSpinner) {
              stickyAddButton.classList.remove('loading');
              stickyLoadingSpinner.classList.add('hidden');
            }
          });
      }

      requestCartJSON(config, variantId) {
        const controller = new AbortController();
        let timeout;
        const request = Promise.resolve()
          .then(() => fetch(`${routes.cart_add_url}`, { ...config, signal: controller.signal }))
          .then(async (response) => {
            const data = await response.json();
            const status = response.ok ? Number(data?.status) : response.status;
            const knownRejection = status >= 400 && status < 500 &&
              [data?.description, data?.message, data?.errors].some(value => typeof value === 'string' && value.trim());
            const addedItems = Array.isArray(data?.items) ? data.items : [data];
            const confirmedAdd = addedItems.some(item => String(item?.variant_id || item?.id) === String(variantId) &&
              Number.isSafeInteger(item?.quantity) && item.quantity > 0);
            if (!knownRejection && (!response.ok || data?.status || data?.errors || !confirmedAdd)) {
              throw new Error('Cart add outcome could not be confirmed');
            }
            if (!response.ok && !data.status) data.status = response.status;
            return data;
          });
        const deadline = new Promise((resolve, reject) => {
          timeout = window.setTimeout(() => {
            controller.abort();
            reject(new Error('Cart request timed out'));
          }, 12000);
        });
        return Promise.race([request, deadline]).finally(() => window.clearTimeout(timeout));
      }

      reconcileRejectedCart() {
        return this.constructor.reconcileRejectedCart(this.cart);
      }

      static reconcileRejectedCart(cart, timeoutMs = 12000) {
        const components = Array.from(document.querySelectorAll('cart-items-component, cart-drawer-items'));
        const owner = cart?.querySelector('cart-drawer-items') || components[0];
        const CartItems = customElements.get('cart-items-component');
        if (!owner?.reconcileRejectedMutation || !CartItems) return Promise.resolve(false);
        const sectionIds = new Set(components.map(component => component.dataset.sectionId).filter(Boolean));
        let controller, timeout, cancelled = false;
        const task = CartItems.mutationQueue.catch(() => {}).then(async () => {
          if (cancelled || !owner.isConnected || CartItems.recoveryRequired) return false;
          controller = owner.createAbortController();
          let focusOwner, focusState;
          const rememberFocus = () => {
            focusOwner = components.find(component => component.contains(document.activeElement)) || owner;
            focusState = focusOwner.captureCartFocus();
          };
          const restoreFocus = () => {
            const edited = focusState?.element;
            if (edited?.matches('input.quantity__input') && edited.value !== edited.defaultValue) {
              const row = Array.from(focusOwner.querySelectorAll('.cart-items__table-row[data-key]'))
                .find(item => item.dataset.key === focusState.key);
              const input = row?.querySelector('input.quantity__input');
              if (input) input.value = edited.value;
            }
            focusOwner.restoreCartFocus(focusState);
          };
          rememberFocus();
          let recoveredCart;
          const rememberCart = event => { recoveredCart = event.detail; };
          document.addEventListener('focusin', rememberFocus, true);
          document.addEventListener('cart:updated', rememberCart);
          try {
            if (!(await owner.reconcileRejectedMutation(sectionIds, controller))) return false;
            restoreFocus();
            if (!recoveredCart) return false;
            const iconUrl = new URL(window.location.href);
            iconUrl.searchParams.set('sections', 'cart-icon-bubble');
            const { response, json: icons } = await owner.fetchCartJSON(iconUrl.toString(), {
              signal: controller.signal, cache: 'no-store',
            });
            if (!response.ok || controller.signal.aborted || owner.activeFetch !== controller || !owner.isConnected) return false;
            const iconDoc = new DOMParser().parseFromString(icons?.['cart-icon-bubble'] || '', 'text/html');
            const icon = iconDoc.getElementById('shopify-section-cart-icon-bubble');
            const count = icon?.querySelector('[data-cart-count]');
            const expected = recoveredCart.item_count < 100 ? String(recoveredCart.item_count) : window.cartStrings.item_99;
            if (!icon || (recoveredCart.item_count ? count?.textContent.trim() !== expected : count)) return false;
            if (cart?.updateCartIconBubble) {
              await cart.updateCartIconBubble({ ...recoveredCart, sections: icons });
              cart.updateCartCounts(recoveredCart.item_count);
            } else {
              document.querySelectorAll('#cart-icon-bubble').forEach(bubble => {
                const trigger = bubble.closest('button, a');
                if (trigger?.querySelector('.cart-text')) return;
                bubble.innerHTML = icon.innerHTML;
                const label = (bubble.querySelector('[data-cart-text]') || bubble.querySelector('.visually-hidden'))?.textContent.trim();
                if (label) trigger?.setAttribute('aria-label', label);
              });
            }
            return true;
          } finally {
            document.removeEventListener('focusin', rememberFocus, true);
            document.removeEventListener('cart:updated', rememberCart);
            if (owner.activeFetch === controller) {
              restoreFocus();
              owner.activeFetch = null;
              if (!owner.pendingQuantityUpdates) owner.classList.remove('cart-items-disabled');
            }
          }
        });
        CartItems.mutationQueue = task;
        const deadline = new Promise(resolve => {
          timeout = window.setTimeout(() => {
            cancelled = true;
            controller?.abort();
            resolve(false);
          }, Math.max(0, Math.min(12000, timeoutMs)));
        });
        return Promise.race([task, deadline]).catch(() => false)
          .finally(() => window.clearTimeout(timeout));
      }

      recoverUncertainCart() {
        if (this.cartNavigationPending) return;
        this.cartNavigationPending = true;
        const restore = (event) => {
          if (!event.persisted) return;
          window.removeEventListener('pageshow', restore);
          cartNavigationPending = false;
          this.cartNavigationPending = false;
          this.submitButton.classList.remove('loading');
          this.submitButton.querySelector('.loading__spinner')?.classList.add('hidden');
          if (!this.error && !this.submitButton.disabled) this.submitButton.removeAttribute('aria-disabled');
          const productInfo = this.closest('product-info');
          const sectionId = productInfo?.dataset.section || productInfo?.dataset.originalSection;
          const sticky = sectionId ? document.getElementById(`StickyCart-ProductSubmitButton-${sectionId}`) : null;
          sticky?.classList.remove('loading');
          sticky?.querySelector('.loading__spinner')?.classList.add('hidden');
        };
        window.addEventListener('pageshow', restore);
        if (cartNavigationPending) return;
        cartNavigationPending = true;
        window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
      }

      disconnectedCallback() {
        try {
          this.cartErrorUnsubscriber?.();
          this._errorObserver?.disconnect();
        } catch (e) {
          // silent
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (!this.submitButton.classList.contains('loading')) {
            this.submitButton.removeAttribute('aria-disabled');
            this.error = false;
          }
          this.submitButtonText.classList.remove('hidden');
          this.submitButton.querySelector('.sold-out-message')?.classList.add('hidden');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
